import { useEffect, useState, useCallback } from 'react';
import { useDatabase } from './useDatabase';

/**
 * Enhanced version of useTableData that provides the same interface
 * but with better performance and error handling
 *
 * This can be used as a drop-in replacement for useTableData
 */
export const useEnhancedTableData = (
  tableName: string,
  idName: string = 'id',
) => {
  const db = useDatabase();
  const [data, setData] = useState<any>([]);
  const [error, setError] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Use the appropriate repository based on table name
      let result;
      switch (tableName) {
        case 'user':
          result = await db.user.getAllData(tableName, true);
          break;
        case 'classes':
          result = await db.class.getAllClasses();
          break;
        case 'students':
          result = await db.student.getAllStudents();
          break;
        case 'semester':
          result = await db.semester.getAllSemesters();
          break;
        default:
          // Fallback to generic method
          result = await db.user.getAllData(tableName, true);
      }

      if (result.error) {
        setError(result.error);
        setData([]);
      } else {
        // Sort data by the specified idName field (preserving original behavior)
        const sortedData =
          result.data?.sort((a: any, b: any) => {
            const aValue = a[idName];
            const bValue = b[idName];
            if (aValue < bValue) return -1;
            if (aValue > bValue) return 1;
            return 0;
          }) || [];

        setData(sortedData);
      }
    } catch (err) {
      setError(err);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [db, tableName, idName]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    error,
    isLoading,
    refetch: fetchData, // Additional utility function for manual refetching
  };
};

/**
 * Enhanced version of useTableSelectedData with same interface
 * but improved performance and error handling
 */
export const useEnhancedTableSelectedData = (
  tableName: string,
  columns: string,
  idName: string = 'id',
) => {
  const db = useDatabase();
  const [data, setData] = useState<any>([]);
  const [error, setError] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      // For selected columns, we need to use the generic query method
      // This preserves the exact behavior of the original function
      const result = await db.user.supabaseClient
        .from(tableName)
        .select(columns)
        .eq('deleted', 'false')
        .order(idName, { ascending: true });

      if (result.error) {
        setError(result.error);
        setData([]);
      } else {
        setData(result.data || []);
      }
    } catch (err) {
      setError(err);
      setData([]);
    } finally {
      setIsLoading(false);
    }
  }, [db, tableName, columns, idName]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    data,
    error,
    isLoading,
    refetch: fetchData,
  };
};

/**
 * Hook for specific data queries that components can opt into
 * Provides better type safety and performance than generic table queries
 */
export const useClassData = (semesterYear?: string) => {
  const db = useDatabase();
  const [data, setData] = useState<any>([]);
  const [error, setError] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const result = semesterYear
          ? await db.class.getClassesBySemester(semesterYear)
          : await db.class.getAllClasses();

        if (result.error) {
          setError(result.error);
          setData([]);
        } else {
          setData(result.data || []);
        }
      } catch (err) {
        setError(err);
        setData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [db.class, semesterYear]);

  return { data, error, isLoading };
};

/**
 * Hook for student data with class filtering
 */
export const useStudentData = (classId?: string) => {
  const db = useDatabase();
  const [data, setData] = useState<any>([]);
  const [error, setError] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const result = classId
          ? await db.student.getStudentsByClassId(classId)
          : await db.student.getAllStudents();

        if (result.error) {
          setError(result.error);
          setData([]);
        } else {
          setData(result.data || []);
        }
      } catch (err) {
        setError(err);
        setData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [db.student, classId]);

  return { data, error, isLoading };
};
